import { MSG } from 'asteroids-shared/protocol'

// WS by message type; ping keeps serverTimeOffset for serverNow / sendInput.t.
export default class ClientSocket {
  #pingInterval = null

  constructor(url = null) {
    this.url = url
    this.ws = null
    this.handlers = new Map()
    this.serverTimeOffset = 0
    this.connected = false
    this.replayBuffer = []
    this.replayActive = false
  }

  connect(url = this.url) {
    this.url = url

    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(this.url)
      this.ws.addEventListener('open', () => {
        this.connected = true
        this.#pingInterval = setInterval(() => this.ping(), 5000)
        resolve()
      })
      this.ws.addEventListener('error', (e) => reject(e))
      this.ws.addEventListener('close', () => {
        this.connected = false
        clearInterval(this.#pingInterval)
        this.#dispatch({ type: 'disconnect' })
      })
      this.ws.addEventListener('message', (ev) => {
        let msg
        try { msg = JSON.parse(ev.data) } catch { return }
        if (msg.type === MSG.PONG) {
          const rtt = Date.now() - msg.t0
          if (Number.isFinite(msg.serverTime)) {
            this.serverTimeOffset = msg.serverTime + rtt / 2 - Date.now()
          }
          const set = this.handlers.get(MSG.PONG)
          if (set) {
            for (const h of set) {
              h({ rtt, serverTimeOffset: this.serverTimeOffset, serverTime: msg.serverTime, t0: msg.t0 })
            }
          }
          return
        }
        if (msg.type === MSG.GAME_START) {
          this.replayBuffer = []
          this.replayActive = true
        } else if (msg.type === MSG.GAME_OVER) {
          this.replayActive = false
          this.replayBuffer = []
        }
        if (this.replayActive && this.#isReplayable(msg.type)) {
          this.replayBuffer.push(msg)
        }
        this.#dispatch(msg)
      })
    })
  }

  on(type, handler) {
    let set = this.handlers.get(type)
    if (!set) { set = new Set(); this.handlers.set(type, set) }
    set.add(handler)
    return () => set.delete(handler)
  }

  #isReplayable(type) {
    return type === MSG.SPAWN_ASTEROID
      || type === MSG.SPAWN_BULLET
      || type === MSG.DESTROY_ASTEROID
      || type === MSG.DESTROY_BULLET
      || type === MSG.SHIP_HIT
      || type === MSG.SHIP_RESPAWN
      || type === MSG.SCORE_UPDATE
      || type === MSG.HUD_FEED
      || type === MSG.SNAPSHOT
  }

  // Dispatch replay buffer to handlers (late subscribe).
  drainReplay() {
    const buf = this.replayBuffer
    this.replayBuffer = []
    this.replayActive = false
    for (const msg of buf) this.#dispatch(msg)
  }

  #dispatch(msg) {
    const set = this.handlers.get(msg.type)
    if (set) for (const h of set) h(msg)
    const wildcard = this.handlers.get('*')
    if (wildcard) for (const h of wildcard) h(msg)
  }

  send(msg) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg))
  }

  // ---- protocol helpers ----------------------------------------------

  setReady(ready) { this.send({ type: MSG.SET_READY, ready }) }
  kickVote(targetId, vote) { this.send({ type: 'kick_vote', targetId, vote }) }
  castVote(vote) {
    this.send({ type: MSG.CAST_VOTE, ...vote })
  }
  returnLobby() { this.send({ type: MSG.RETURN_LOBBY }) }
  ringLobbyBell() { this.send({ type: MSG.RING_BELL }) }
  // t = server-ish epoch (Date.now + offset) for lag-comp.
  sendInput(seq, keys) { this.send({ type: MSG.INPUT, seq, keys, t: Date.now() + this.serverTimeOffset }) }
  leaveRoom() { this.send({ type: MSG.LEAVE_ROOM }) }
  ping() { this.send({ type: MSG.PING, t0: Date.now() }) }

  serverNow() { return Date.now() + this.serverTimeOffset }

  close() {
    clearInterval(this.#pingInterval)
    try { this.ws?.close() } catch {}
  }
}
